import React, {useEffect, useState} from 'react';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import {useIntl} from 'react-intl';

import Loader from '../../components/dashboard/Loader';
import API from '../../common/utils/API';
import {DateTimeCell} from '../../components/relevance/formatDateTime';
import KillSwitchConfirmModal from '../../components/killSwitches/KillSwitchConfirmModal';
import PaymentsInFlight from '../../components/killSwitches/PaymentsInFlight';

// Única clave con tratamiento especial: el contador de compras en curso.
const PAYMENTS_KEY = 'payments';

/**
 * `updatedBy` llega como usuario o null; se toleran campos faltantes para no
 * atarse a la forma exacta del backend.
 */
export function formatUpdatedBy(updatedBy) {
    if (!updatedBy) return '-';
    if (typeof updatedBy !== 'object') return String(updatedBy);
    if (updatedBy.name) return updatedBy.name;
    if (updatedBy.email) return updatedBy.email;
    if (updatedBy.id !== undefined && updatedBy.id !== null) {
        return `#${updatedBy.id}`;
    }
    return '-';
}

/**
 * Pantalla genérica: pinta cada clave que devuelva el backend, así que una
 * clave nueva aparece sin cambios en el admin.
 */
function KillSwitchesPage() {
    const intl = useIntl();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [target, setTarget] = useState(null);

    async function fetchList() {
        setLoading(true);

        try {
            const list = await API.killSwitches.list();
            setItems(Array.isArray(list) ? list : []);
            setError(false);
        } catch (e) {
            setError(true);
        }

        setLoading(false);
    }

    useEffect(() => {
        fetchList();
    }, []);

    async function handleConfirmed(changed, updated) {
        setTarget(null);

        // El PUT devuelve el registro actualizado; si no trae la forma
        // esperada se recarga la lista para mostrar el valor real.
        if (updated && updated.key === changed.key) {
            setItems((previous) =>
                previous.map((item) =>
                    item.key === updated.key ? {...item, ...updated} : item,
                ),
            );
        } else {
            await fetchList();
        }
    }

    return (
        <div
            data-test='killSwitchesPageComponent'
            style={{position: 'relative'}}
        >
            <Loader loading={loading} />
            {error && (
                <Alert variant='danger' data-test='killSwitchesLoadError'>
                    {intl.formatMessage({id: 'KILL_SWITCHES.LOAD_ERROR'})}
                </Alert>
            )}
            {!loading && !error && items.length === 0 ? (
                <p data-test='killSwitchesEmptyState'>
                    {intl.formatMessage({id: 'KILL_SWITCHES.EMPTY'})}
                </p>
            ) : (
                <Card>
                    <Card.Body>
                        <Table striped bordered size='sm'>
                            <thead>
                                <tr>
                                    <th>
                                        {intl.formatMessage({
                                            id: 'KILL_SWITCHES.COLUMN_KEY',
                                        })}
                                    </th>
                                    <th>
                                        {intl.formatMessage({
                                            id:
                                                'KILL_SWITCHES.COLUMN_DESCRIPTION',
                                        })}
                                    </th>
                                    <th>
                                        {intl.formatMessage({
                                            id: 'KILL_SWITCHES.COLUMN_STATUS',
                                        })}
                                    </th>
                                    <th>
                                        {intl.formatMessage({
                                            id:
                                                'KILL_SWITCHES.COLUMN_UPDATED_BY',
                                        })}
                                    </th>
                                    <th>
                                        {intl.formatMessage({
                                            id:
                                                'KILL_SWITCHES.COLUMN_UPDATED_AT',
                                        })}
                                    </th>
                                    <th>
                                        {intl.formatMessage({
                                            id: 'KILL_SWITCHES.COLUMN_REASON',
                                        })}
                                    </th>
                                    <th>
                                        {intl.formatMessage({
                                            id: 'KILL_SWITCHES.COLUMN_ACTIONS',
                                        })}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr
                                        key={item.key}
                                        data-test='killSwitchRow'
                                    >
                                        <td>
                                            <code>{item.key}</code>
                                        </td>
                                        <td>{item.description || '-'}</td>
                                        <td>
                                            <Badge
                                                variant={
                                                    item.enabled
                                                        ? 'success'
                                                        : 'danger'
                                                }
                                                data-test='killSwitchStatus'
                                            >
                                                {intl.formatMessage({
                                                    id: item.enabled
                                                        ? 'KILL_SWITCHES.STATUS_ENABLED'
                                                        : 'KILL_SWITCHES.STATUS_DISABLED',
                                                })}
                                            </Badge>
                                            {item.key === PAYMENTS_KEY && (
                                                <PaymentsInFlight
                                                    enabled={item.enabled}
                                                />
                                            )}
                                        </td>
                                        <td data-test='killSwitchUpdatedBy'>
                                            {formatUpdatedBy(item.updatedBy)}
                                        </td>
                                        <td>
                                            <DateTimeCell
                                                iso={item.updatedAt}
                                            />
                                        </td>
                                        <td data-test='killSwitchReason'>
                                            {item.reason || '-'}
                                        </td>
                                        <td>
                                            <Button
                                                size='sm'
                                                variant={
                                                    item.enabled
                                                        ? 'danger'
                                                        : 'primary'
                                                }
                                                onClick={() =>
                                                    setTarget({
                                                        key: item.key,
                                                        enabled: !item.enabled,
                                                    })
                                                }
                                                data-test='killSwitchToggleButton'
                                            >
                                                {intl.formatMessage({
                                                    id: item.enabled
                                                        ? 'KILL_SWITCHES.ACTION_DISABLE'
                                                        : 'KILL_SWITCHES.ACTION_ENABLE',
                                                })}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
            {target && (
                <KillSwitchConfirmModal
                    target={target}
                    onHide={() => setTarget(null)}
                    onConfirmed={handleConfirmed}
                />
            )}
        </div>
    );
}

export default KillSwitchesPage;
